import { createClient } from '@supabase/supabase-js'

/**
 * AIQ Studio — Supabase Client
 * Initializes only when valid credentials are present
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Guard: don't crash if credentials aren't set yet
const isConfigured =
  supabaseUrl &&
  supabaseAnonKey &&
  supabaseUrl.startsWith('http') &&
  !supabaseUrl.includes('PASTE_')

export const supabase = isConfigured
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null

const notConfigured = () => {
  throw new Error('Supabase not configured. Add your URL and anon key to .env then restart the dev server.')
}

// ─── Characters ─────────────────────────────────────────────────────────────

export async function fetchCharacters() {
  if (!supabase) return []
  const { data, error } = await supabase
    .from('characters')
    .select('*')
    .order('created_at', { ascending: false })
  if (error) throw error
  return data
}

export async function createCharacter({ name, triggerWord, loraPath, thumbnailUrl }) {
  if (!supabase) notConfigured()
  const { data, error } = await supabase
    .from('characters')
    .insert({ name, trigger_word: triggerWord, lora_path: loraPath, thumbnail_url: thumbnailUrl })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function updateCharacter(id, updates) {
  if (!supabase) notConfigured()
  const { data, error } = await supabase
    .from('characters')
    .update(updates)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data
}

// ─── Images ─────────────────────────────────────────────────────────────────

export async function fetchImages({ limit = 50, offset = 0, characterId = null } = {}) {
  if (!supabase) return []
  let query = supabase
    .from('images')
    .select('*')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)
  if (characterId) query = query.eq('character_id', characterId)
  const { data, error } = await query
  if (error) throw error
  return data
}

export async function saveImage({ characterId, prompt, imageUrl, resolution, cost, tags = [] }) {
  if (!supabase) notConfigured()
  const { data, error } = await supabase
    .from('images')
    .insert({ character_id: characterId, prompt, image_url: imageUrl, resolution, cost, tags })
    .select()
    .single()
  if (error) throw error
  return data
}

// ─── Clips ──────────────────────────────────────────────────────────────────

export async function fetchClips({ limit = 50, offset = 0, characterId = null } = {}) {
  if (!supabase) return []
  let query = supabase
    .from('clips')
    .select('*')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)
  if (characterId) query = query.eq('character_id', characterId)
  const { data, error } = await query
  if (error) throw error
  return data
}

export async function saveClip({ characterId, prompt, model, videoUrl, cost, tags = [], duration }) {
  if (!supabase) notConfigured()
  const { data, error } = await supabase
    .from('clips')
    .insert({ character_id: characterId, prompt, model, video_url: videoUrl, cost, tags, duration })
    .select()
    .single()
  if (error) throw error
  return data
}

// ─── Storyboards ────────────────────────────────────────────────────────────

export async function fetchStoryboards() {
  if (!supabase) return []
  const { data, error } = await supabase
    .from('storyboards')
    .select('*')
    .order('created_at', { ascending: false })
  if (error) throw error
  return data
}

export async function saveStoryboard({ title, script, scenes, status = 'draft' }) {
  if (!supabase) notConfigured()
  const { data, error } = await supabase
    .from('storyboards')
    .insert({ title, script, scenes, status })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function updateStoryboard(id, updates) {
  if (!supabase) notConfigured()
  const { data, error } = await supabase
    .from('storyboards')
    .update(updates)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data
}

// ─── Projects ────────────────────────────────────────────────────────────────

export async function fetchProjects() {
  if (!supabase) return []
  const { data, error } = await supabase
    .from('projects')
    .select('*, storyboards(title)')
    .order('created_at', { ascending: false })
  if (error) throw error
  return data
}
